import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import moment from 'moment';

export interface SortConfig {
    label: string;
    value: string;
    sortBy: string;
    sortOrder: string;
}

export interface SearchEvent {
    fromDate: Date | null;
    toDate: Date | null;
    customerPurchaseNumber: string;
    offerNumber: string;
    sortBy: string;
    sortOrder: string;
}

type SearchForm = {
    start: FormControl<Date | null>;
    end: FormControl<Date | null>;
    searchValue: FormControl<string>;
    customerPurchaseNumber: FormControl<string>;
    offerNumber: FormControl<string>;
    selectedSort: FormControl<string | null>;
};

type ControlName = 'searchValue' | 'customerPurchaseNumber' | 'offerNumber';

type FieldDef = Readonly<{
    control: ControlName;
    placeholder: string;
}>;

export type OfferSearchType = 'offerNumber' | 'quickRef' | null;

export function getOfferSearchType(value: string | null | undefined): OfferSearchType {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
        return null;
    }
    if (/^\d{10}$/.test(trimmedValue)) {
        return 'offerNumber';
    }
    if (/^(?=.*[a-zA-Z])[a-zA-Z0-9]{5}$/.test(trimmedValue)) {
        return 'quickRef';
    }
    return null;
}

export const offerNumberOrQuickRefValidator: ValidatorFn = (
    control: AbstractControl<string | null>
): ValidationErrors | null => {
    const value = control.value?.trim();
    if (!value) {
        return null;
    }
    return getOfferSearchType(value) ? null : { invalidOfferNumberOrQuickRef: true };
};

@Component({
    selector: 'app-search-and-filter',
    templateUrl: './search-and-filter.component.html',
    styleUrls: ['./search-and-filter.component.scss'],
})
export class SearchAndFilterComponent {
    @Input() selectedTab: string;
    @Output() offerSearch = new EventEmitter<SearchEvent>();
    maxDate = moment().utc();
    form = new FormGroup<SearchForm>({
        start: new FormControl<Date | null>(null),
        end: new FormControl<Date | null>(null),
        searchValue: new FormControl<string>(''),
        customerPurchaseNumber: new FormControl<string>(''),
        offerNumber: new FormControl<string>('', [offerNumberOrQuickRefValidator]),
        selectedSort: new FormControl<string>('createdDate_desc', { nonNullable: true }),
    });

    public sortOptions: SortConfig[] = [
        {
            label: 'OVERVIEW.TABLE.COLUMN.CREATED_AT',
            value: 'createdDate_desc',
            sortBy: 'createdDate',
            sortOrder: 'desc',
        },
        {
            label: 'OVERVIEW.TABLE.COLUMN.CREATED_AT',
            value: 'createdDate_asc',
            sortBy: 'createdDate',
            sortOrder: 'asc',
        },
        {
            label: 'OVERVIEW.TABLE.COLUMN.NAME',
            value: 'title_asc',
            sortBy: 'title',
            sortOrder: 'asc',
        },
        {
            label: 'OVERVIEW.TABLE.COLUMN.NAME',
            value: 'title_desc',
            sortBy: 'title',
            sortOrder: 'desc',
        },
    ];

    offerFields: FieldDef[] = [
        { control: 'customerPurchaseNumber', placeholder: 'OVERVIEW.CUSTOMER_PURCHASE_NUMBER_PLACEHOLDER' },
        { control: 'offerNumber', placeholder: 'OVERVIEW.OFFER_NUMBER_PLACEHOLDER' },
    ];
    templateFields: FieldDef[] = [{ control: 'searchValue', placeholder: 'OVERVIEW.SEARCH_PLACEHOLDER' }];

    get isOffersTab() {
        return this.selectedTab === 'offers';
    }

    get activeSearchFields() {
        return this.isOffersTab ? this.offerFields : this.templateFields;
    }

    setOfferNumberMaxLength() {
        const value = this.form.controls.offerNumber.value?.trim() ?? '';
        return /[a-zA-Z]/.test(value) ? 5 : 10;
    }

    resetDate() {
        this.form.controls.start.setValue(null);
        this.form.controls.end.setValue(null);
        this.triggerSearch();
    }

    resetField(controlName: ControlName) {
        this.form.controls[controlName].setValue('');
        this.triggerSearch();
    }

    hasAtLeastOneValue(): boolean {
        const hasDateRange = !!this.form.controls.start.value && !!this.form.controls.end.value;
        const hasOffersText =
            !!this.form.controls.customerPurchaseNumber.value?.trim() || !!this.form.controls.offerNumber.value?.trim();
        const hasGenericText = !!this.form.controls.searchValue.value?.trim();
        const hasAtLeastOneValue = this.isOffersTab ? hasOffersText || hasDateRange : hasGenericText || hasDateRange;
        return hasAtLeastOneValue && this.form.valid;
    }

    triggerSearch() {
        const [sortBy, sortOrder] = this.form.controls.selectedSort.value.split('_');
        const { start, end, offerNumber } = this.form.controls;
        const toDate = end.value ?? new Date();
        const toDatePlus1 = moment(toDate).add(1, 'days').startOf('day').toDate();

        const customerPurchaseNumber = this.form.controls.customerPurchaseNumber.value.trim();
        const offerSearchValue = offerNumber.value.trim();
        const offerSearchType = getOfferSearchType(offerSearchValue);

        const payload = {
            fromDate: start.value ?? null,
            toDate: toDatePlus1,
            customerPurchaseNumber: customerPurchaseNumber
                ? `*${customerPurchaseNumber.replace(/\s/g, '*')}*`
                : undefined,
            offerNumber: offerSearchType === 'offerNumber' ? offerSearchValue : undefined,
            quickRef: offerSearchType === 'quickRef' ? offerSearchValue : undefined,
            sortBy,
            sortOrder,
        };
        this.offerSearch.emit(payload);
    }
}
